import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { useDispatch } from "react-redux";
import {
  CustomFormatItem,
  JobType,
} from "../../../docuclipper/DocuclipperTypes";
import {
  updateCustomFormatItemRedux,
  deleteCustomFormatItemRedux,
} from "../../../redux/reducers/CustomFormat";

type Props = {
  cfi: CustomFormatItem;
  jobType: JobType;
  previewValue?: string | number;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
};

export const CustomFormatItemComp: React.FC<Props> = ({
  cfi,
  jobType,
  previewValue,
  dragHandleProps,
}) => {
  const dispatch = useDispatch();
  const getOptions = () => {
    const commonOptions = [
      { value: "autoIncrement", label: "Auto Increment" },
      { value: "transactionType", label: "Transaction Type" },
      { value: "hardcodedValue", label: "Hardcoded Value" },
    ];

    if (jobType === "Invoice") {
      return [
        { value: "id", label: "Invoice Number" },
        { value: "date", label: "Invoice Date" },
        { value: "dueDate", label: "Invoice Due Date" },
        { value: "tax", label: "Tax Amount" },
        { value: "total", label: "Invoice Total" },
        { value: "item", label: "Line Item Description" },
        { value: "quantity", label: "Line Item Quantity" },
        { value: "unitPrice", label: "Unit Price per Item" },
        { value: "price", label: "Line Item Total" },
        { value: "service", label: "Service Description" },
        { value: "category", label: "Item/Service Category" },
        { value: "customer", label: "Customer/Bill To" },
        { value: "vendor", label: "Vendor/From" },
        { value: "poNumber", label: "Purchase Order Number" },
        { value: "sourcePdfLink", label: "Source PDF Link" },
      ];
    }

    return [
      { value: "date", label: "Date" },
      { value: "date2", label: "Date2" },
      { value: "payee", label: "Payee" },
      { value: "name", label: "Description" },
      { value: "amount", label: "Amount" },
      { value: "credit", label: "Credit" },
      { value: "debit", label: "Debit" },
      { value: "balance", label: "Balance" },
      { value: "checkNumber", label: "Check Number" },
      { value: "refNumber", label: "Reference Number" },
      { value: "account", label: "Account" },
      { value: "category", label: "Category" },
      { value: "categoryKeyword", label: "Category Keyword" },
      { value: "fileName", label: "File Name" },
      { value: "pageNumber", label: "Page Number" },
      { value: "metadata", label: "Metadata" },
      ...commonOptions,
    ];
  };

  const findLabelForSource = (source: string | undefined) => {
    if (!source) return undefined;
    const options = getOptions();
    const option = options.find((opt) => opt.value === source);
    return option?.label;
  };

  const [localHeading, setLocalHeading] = useState(
    cfi.heading?.text ||
      findLabelForSource(cfi.source) ||
      `Column ${cfi.index + 1}`
  );
  const [localCreditLabel, setLocalCreditLabel] = useState(
    cfi.creditLabel || "Credit"
  );
  const [localDebitLabel, setLocalDebitLabel] = useState(
    cfi.debitLabel || "Debit"
  );
  const [localHardcodedValue, setLocalHardcodedValue] = useState(
    cfi.hardcodedValue || ""
  );

  const persistHeading = (text: string) =>
    dispatch(updateCustomFormatItemRedux({ ...cfi, heading: { text } }));

  // Changing the Field auto-renames the column ONLY when the user hasn't
  // customized the heading (it still matches the previous field's default
  // label or is empty/placeholder). Custom headings are left untouched.
  const handleFieldChange = (newSource: string) => {
    const oldDefault = findLabelForSource(cfi.source);
    const isUntouched =
      !localHeading ||
      localHeading === oldDefault ||
      localHeading === `Column ${cfi.index + 1}`;
    const newDefault =
      findLabelForSource(newSource) || `Column ${cfi.index + 1}`;
    const nextHeading = isUntouched ? newDefault : localHeading;
    if (isUntouched) {
      setLocalHeading(nextHeading);
    }
    dispatch(
      updateCustomFormatItemRedux({
        ...cfi,
        source: newSource as any,
        heading: { text: nextHeading },
      })
    );
  };

  return (
    <div>
      {/* Heading doubles as the column's name — edit it inline to rename. */}
      <div className="d-flex align-items-center mb-1">
        <Form.Control
          type="text"
          value={localHeading}
          onChange={(e) => setLocalHeading(e.target.value)}
          onBlur={() => persistHeading(localHeading)}
          aria-label="Column name"
          title="Column name — the header text shown in your export"
          placeholder="Column name"
          style={{
            fontWeight: 600,
            border: "none",
            borderBottom: "1px dashed #adb5bd",
            borderRadius: 0,
            padding: "2px 0",
            boxShadow: "none",
            backgroundColor: "transparent",
          }}
        />
        <FontAwesomeIcon icon="pencil-alt" className="text-muted ml-2" />
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        {dragHandleProps ? (
          <span
            {...dragHandleProps}
            className="text-muted"
            style={{ cursor: "grab" }}
            title="Drag to reorder column"
          >
            <FontAwesomeIcon icon="arrows-alt" />
            <span className="ml-1 small">Drag</span>
          </span>
        ) : (
          <span />
        )}
        <Button
          variant="link"
          className="p-0 text-danger"
          onClick={() => dispatch(deleteCustomFormatItemRedux(cfi))}
          title="Delete column"
        >
          <FontAwesomeIcon icon="trash" />
        </Button>
      </div>

      <Form.Group className="mb-2">
        <Form.Label className="small text-muted mb-1">Field</Form.Label>
        <Form.Control
          as="select"
          value={cfi.source}
          onChange={(e) => handleFieldChange(e.target.value)}
        >
          {getOptions().map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {cfi.source === "transactionType" && (
        <>
          <Form.Group>
            <Form.Label>Credit Label</Form.Label>
            <Form.Control
              type="text"
              value={localCreditLabel}
              onChange={(e) => setLocalCreditLabel(e.target.value)}
              onBlur={() =>
                dispatch(
                  updateCustomFormatItemRedux({
                    ...cfi,
                    creditLabel: localCreditLabel,
                  })
                )
              }
              placeholder="E.g. Deposit"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Debit Label</Form.Label>
            <Form.Control
              type="text"
              value={localDebitLabel}
              onChange={(e) => setLocalDebitLabel(e.target.value)}
              onBlur={() =>
                dispatch(
                  updateCustomFormatItemRedux({
                    ...cfi,
                    debitLabel: localDebitLabel,
                  })
                )
              }
              placeholder="E.g. Payment"
            />
          </Form.Group>
        </>
      )}

      {cfi.source === "hardcodedValue" && (
        <Form.Group>
          <Form.Label>Value</Form.Label>
          <Form.Control
            type="text"
            value={localHardcodedValue}
            onChange={(e) => setLocalHardcodedValue(e.target.value)}
            onBlur={() =>
              dispatch(
                updateCustomFormatItemRedux({
                  ...cfi,
                  hardcodedValue: localHardcodedValue,
                })
              )
            }
            placeholder="Enter hardcoded value"
          />
        </Form.Group>
      )}

      <div className="mt-2 pt-2 border-top">
        <div className="small text-muted">Sample value</div>
        <div className="text-truncate" title={String(previewValue ?? "")}>
          {previewValue === "" || previewValue === undefined ? (
            <span className="text-muted">—</span>
          ) : (
            previewValue
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomFormatItemComp;
